/**
 * Calculates the total amount and details of features in the addons based on the plan metadata.
 *
 * @param {Array} addons - The array of addons.
 * @param {Object} planMetadata - The metadata of the plan.
 * @param {Object} addonsFeatures - The addonsFeatures purchased by the user.
 * @returns {Object} - The total features amount and details.
 * @property {number} addonsAmount - The total amount of addons.
 * @property {number} storageAmount - The total amount of storage addons.
 * @property {Array} addons - The array of addon details.
 */
const getTotalFeaturesAmount = (addons, planMetadata, addonsFeatures) => {
  const totalFeaturesAmount = addons.reduce((acc, addon) => {
    const featureType = addon.metadata?.['ui:featureType'] || addon.metadata?.type;
    const featureName = addon.metadata?.featureName;
    let featureValue = addon.metadata?.[featureName];

    if (featureType === 'tiered_addon' && !featureValue) {
      featureValue = addonsFeatures[featureName] ? `${addonsFeatures[featureName].id}===true` : `${addon.tiers[0]?.id}===false`;
    }

    const isNotExtraAddon = planMetadata[featureName]?.toString() === featureValue?.toString()
      || (!!planMetadata[featureName])?.toString() === featureValue?.toString()?.split('===')[1];

    if (planMetadata[featureName] === undefined && featureType !== 'tiered_addon') {
      return acc;
    }

    if (addon?.isPlaceholder) {
      acc.addons.push({
        featureName,
        value: planMetadata[featureName],
        id: null,
      });
      return acc;
    }

    if (featureName === 'maxGB') {
      acc.storageAmount += addon.extraAmount;
      acc.addons.push({
        featureName,
        value: featureValue,
        id: addon.id,
      });
      return acc;
    }

    if (featureType === 'boolean'
      && featureValue?.toString() === 'true'
      && !addon?.isPlaceholder) {
      const addonExtraAmount = acc.addonsAmount + addon.amount;
      acc.addonsAmount = !isNotExtraAddon ? addonExtraAmount : acc.addonsAmount;
      acc.addons.push({
        featureName,
        value: true,
        id: addon.id,
      });
      return acc;
    } if (featureType === 'boolean'
      && featureValue?.toString() === 'false'
      && !addon?.isPlaceholder) {
      const addonExtraAmount = acc.addonsAmount - addon.amount;
      acc.addonsAmount = !isNotExtraAddon ? addonExtraAmount : acc.addonsAmount;
      acc.addons.push({
        featureName,
        value: false,
        id: addon.id,
      });
      return acc;
    }

    if (featureType === 'number') {
      const addonExtraAmount = acc.addonsAmount + (addon.amount * (featureValue - Number(planMetadata[featureName])));
      acc.addonsAmount = !isNotExtraAddon ? addonExtraAmount : acc.addonsAmount;
      acc.addons.push({
        featureName,
        value: featureValue,
        id: addon.id,
      });
      return acc;
    }

    if (featureType === 'tiered_addon'
      && featureValue?.toString().endsWith('true')
      && !addon?.isPlaceholder) {
      const [id, value] = (featureValue || '').split('===');
      const currentTierAmount = addon.tiers.find((tier) => tier.id === id)?.amount;
      const addonExtraAmount = acc.addonsAmount + (currentTierAmount || 0);
      acc.addonsAmount = !isNotExtraAddon ? addonExtraAmount : acc.addonsAmount;
      acc.addons.push({
        featureName,
        value: value === 'true',
        id,
      });
      return acc;
    } if (featureType === 'tiered_addon'
      && featureValue?.toString().endsWith('false')
      && !addon?.isPlaceholder) {
      const [id] = (featureValue || '').split('===');
      const currentTierAmount = addon.tiers.find((tier) => tier.id === id)?.amount;
      const addonExtraAmount = acc.addonsAmount - (currentTierAmount || 0);
      acc.addonsAmount = !isNotExtraAddon ? addonExtraAmount : acc.addonsAmount;
      acc.addons.push({
        featureName,
        value: false,
        id,
      });
      return acc;
    }
    return acc;
  }, {
    addonsAmount: 0,
    storageAmount: 0,
    addons: [],
  });

  return totalFeaturesAmount;
};

export default getTotalFeaturesAmount;
